import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, emitPostsChanged } from "@/lib/api";

const Ctx = createContext(null);
export const useDrag = () => useContext(Ctx);
const LONG_PRESS = 380;
const fmt = (d) => new Date(`${d}T12:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

async function moveTo(post, date, silent) {
  const old = post.scheduled_at?.slice(0, 10);
  try {
    await api.post(`/posts/${post.id}/reschedule`, { date });
    emitPostsChanged();
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    if (!silent) toast.success(`Verschoben auf ${fmt(date)}`, { action: old && old >= todayKey ? { label: "Rückgängig", onClick: () => moveTo(post, old, true) } : undefined });
  } catch (e) { toast.error(errMsg(e)); }
}

export function DragProvider({ children }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const st = useRef({});
  const suppress = useRef(false);

  const targetAt = (x, y) => document.elementFromPoint(x, y)?.closest("[data-drop-date]")?.getAttribute("data-drop-date") || null;

  const start = useCallback((e, post, file) => {
    if (e.button && e.button !== 0) return;
    const s = { post, file, x0: e.clientX, y0: e.clientY, touch: e.pointerType !== "mouse", active: false };
    const zone = e.currentTarget.closest("[data-drag-zone]");
    const begin = () => { s.active = true; navigator.vibrate?.(15); setDrag({ post, file, x: s.x, y: s.y, zone: zone || null }); };
    s.x = e.clientX; s.y = e.clientY;
    if (s.touch) s.timer = setTimeout(begin, LONG_PRESS);
    s.begin = begin;
    st.current = s;
  }, []);

  useEffect(() => {
    const move = (e) => {
      const s = st.current;
      if (!s.post) return;
      s.x = e.clientX; s.y = e.clientY;
      const dist = Math.hypot(e.clientX - s.x0, e.clientY - s.y0);
      if (!s.active) {
        if (s.touch && dist > 10) { clearTimeout(s.timer); st.current = {}; }
        else if (!s.touch && dist > 6) s.begin();
        return;
      }
      setDrag((d) => d && { ...d, x: e.clientX, y: e.clientY });
      setOver(targetAt(e.clientX, e.clientY));
      const edge = 70;
      clearInterval(s.scroll);
      const dy = e.clientY < edge ? -14 : e.clientY > window.innerHeight - edge ? 14 : 0;
      if (dy && !document.querySelector("[data-testid='drag-day-sheet']")) s.scroll = setInterval(() => { window.scrollBy(0, dy); setDrag((d) => d && { ...d }); setOver(targetAt(s.x, s.y)); }, 16);
    };
    const up = (e) => {
      const s = st.current;
      clearTimeout(s.timer);
      clearInterval(s.scroll);
      if (s.active) {
        const t = targetAt(e.clientX, e.clientY);
        if (e.type === "pointerup" && t && t !== s.post.scheduled_at?.slice(0, 10)) moveTo(s.post, t);
        suppress.current = true;
        setTimeout(() => { suppress.current = false; }, 80);
      }
      st.current = {};
      setDrag(null); setOver(null);
    };
    const block = (e) => { if (st.current.active) e.preventDefault(); };
    const click = (e) => { if (suppress.current) { e.stopPropagation(); e.preventDefault(); } };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("touchmove", block, { passive: false });
    window.addEventListener("click", click, true);
    window.addEventListener("contextmenu", block);
    return () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      window.removeEventListener("touchmove", block); window.removeEventListener("click", click, true); window.removeEventListener("contextmenu", block);
    };
  }, []);

  return <Ctx.Provider value={{ drag, over, start }}>{children}</Ctx.Provider>;
}

export const dragProps = (ctx, post, file, enabled = true) => (enabled && ctx && post.status !== "published" ? {
  onPointerDown: (e) => ctx.start(e, post, file),
  style: { WebkitTouchCallout: "none", touchAction: "manipulation" },
  draggable: false,
} : {});
